import { resolveMediaUrl } from "../../lib/utils";

/**
 * Small DOM helpers to create full-screen overlays modals for the experiment
 */
function normalizeForwardText(text) {
  if (typeof text !== "string") return "";
  return text
    .replace(/\s*->\s*\]/g, " ]")
    .replace(/\s*->\s*$/g, "")
    .trim();
}

function appendForwardLabelWithArrow(target, text, arrowHeightPx = 12, useWhiteArrow = false) {
  const label = document.createElement("span");
  label.textContent = normalizeForwardText(text);

  const arrow = document.createElement("img");
  arrow.src = "assets/images/instructions/rightInstructionArrow.svg";
  arrow.alt = "Right arrow";
  arrow.style.height = `${arrowHeightPx}px`;
  arrow.style.width = "auto";
  arrow.style.transform = "translateY(1px)";
  if (useWhiteArrow) {
    arrow.style.filter = "brightness(0) saturate(100%) invert(100%)";
  }

  target.appendChild(label);
  target.appendChild(arrow);
}

export function createReadyOverlay({
  imageOrVideoUrl = null,
  onStart,
  onBack: _onBack,
  forwardText,
  backText,
} = {}) {
  // Touch unused optional param to satisfy linter
  void _onBack;
  // Create the main overlay backdrop
  const overlay = document.createElement("div");
  overlay.id = "gear-ready-overlay";
  overlay.style.position = "fixed";
  overlay.style.top = "0";
  overlay.style.left = "0";
  overlay.style.width = "100%";
  overlay.style.height = "100%";
  overlay.style.display = "flex";
  overlay.style.flexDirection = "column";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.background = "rgba(0, 0, 0, 0.6)";
  overlay.style.zIndex = "10000";

  // Create the Card Container
  const container = document.createElement("div");
  container.style.backgroundImage = "url('assets/images/instructions/tutorial_tv_placeholder.png')";
  container.style.backgroundSize = "contain";
  container.style.backgroundRepeat = "no-repeat";
  container.style.backgroundPosition = "center";
  // container.style.padding = "60px 20px 80px 20px";
  container.style.display = "flex";
  container.style.flexDirection = "column";
  container.style.alignItems = "center";
  container.style.justifyContent = "center";
  container.style.minWidth = "705px";
  container.style.minHeight = "560px";

  // The Image/Video mockup
  let videoElement = null;
  let slashArmed = false;
  if (imageOrVideoUrl) {
    const resolvedUrl = resolveMediaUrl(imageOrVideoUrl);
    // Check if it's a video file based on extension
    const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(resolvedUrl);

    if (isVideo) {
      // Create video wrapper for custom controls
      const videoWrapper = document.createElement("div");
      videoWrapper.style.position = "relative";
      videoWrapper.style.width = "416px";
      videoWrapper.style.height = "252px";
      videoWrapper.style.marginTop = "-30px";
      videoWrapper.style.marginBottom = "5px";
      videoWrapper.style.marginRight = "0px";

      // Create video element without native controls
      const video = document.createElement("video");
      video.src = resolvedUrl;
      video.controls = false;
      video.preload = "metadata";
      video.playsInline = true;
      video.style.width = "100%";
      video.style.height = "100%";
      video.style.display = "block";
      video.style.objectFit = "contain";
      video.style.borderRadius = "8px";
      video.style.backgroundColor = "transparent";

      // Create custom play button
      const playButton = document.createElement("div");
      playButton.style.position = "absolute";
      playButton.style.top = "50%";
      playButton.style.left = "50%";
      playButton.style.transform = "translate(-50%, -50%)";
      playButton.style.width = "60px";
      playButton.style.height = "60px";
      playButton.style.backgroundColor = "#56FEFF";
      playButton.style.borderRadius = "50%";
      playButton.style.cursor = "pointer";
      playButton.style.display = "flex";
      playButton.style.alignItems = "center";
      playButton.style.justifyContent = "center";
      playButton.style.transition = "all 0.3s ease";
      playButton.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.3)";
      playButton.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style="margin-left: 1px;">
          <path d="M8 5v14l11-7z" fill="#ffff"/>
        </svg>
      `;

      // Play button hover effect
      playButton.addEventListener("mouseenter", () => {
        playButton.style.backgroundColor = "#6DFFFF";
        playButton.style.transform = "translate(-50%, -50%) scale(1.1)";
      });
      playButton.addEventListener("mouseleave", () => {
        playButton.style.backgroundColor = "#56FEFF";
        playButton.style.transform = "translate(-50%, -50%) scale(1)";
      });

      // Play/pause functionality
      playButton.addEventListener("click", () => {
        if (video.paused) {
          video.play();
          playButton.style.display = "none";
        }
      });

      video.addEventListener("click", () => {
        if (!video.paused) {
          video.pause();
          playButton.style.display = "flex";
        }
      });

      video.addEventListener("play", () => {
        playButton.style.display = "none";
      });

      video.addEventListener("pause", () => {
        playButton.style.display = "flex";
      });

      video.addEventListener("ended", () => {
        playButton.style.display = "flex";
      });

      // Create fullscreen button
      // const fullscreenButton = document.createElement("div");
      // fullscreenButton.style.position = "absolute";
      // fullscreenButton.style.top = "10px";
      // fullscreenButton.style.right = "10px";
      // fullscreenButton.style.width = "36px";
      // fullscreenButton.style.height = "36px";
      // fullscreenButton.style.cursor = "pointer";
      // fullscreenButton.style.display = "flex";
      // fullscreenButton.style.alignItems = "center";
      // fullscreenButton.style.justifyContent = "center";
      // fullscreenButton.style.transition = "all 0.3s ease";
      // fullscreenButton.innerHTML = `
      //   <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      //     <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" fill="#ffff" />
      //   </svg>
      // `;

      // // Fullscreen button hover effect
      // fullscreenButton.addEventListener("mouseenter", () => {
      //   fullscreenButton.style.transform = "scale(1.15)";
      // });
      // fullscreenButton.addEventListener("mouseleave", () => {
      //   fullscreenButton.style.transform = "scale(1)";
      // });

      // // Fullscreen functionality
      // fullscreenButton.addEventListener("click", () => {
      //   if (!document.fullscreenElement) {
      //     video.requestFullscreen().catch(err => {
      //       console.log(`Error attempting to enable fullscreen: ${err.message}`);
      //     });
      //   } else {
      //     document.exitFullscreen();
      //   }
      // });

      videoWrapper.appendChild(video);
      videoWrapper.appendChild(playButton);
      // videoWrapper.appendChild(fullscreenButton);
      container.appendChild(videoWrapper);
      videoElement = video;
    } else {
      const img = document.createElement("img");
      img.src = resolvedUrl;
      img.style.maxWidth = "100%";
      img.style.maxHeight = "70vh";
      img.style.display = "block";
      img.style.objectFit = "contain";
      img.style.borderRadius = "8px";

      container.appendChild(img);
    }
  }

  // Navigation Text Row
  const navRow = document.createElement("div");
  navRow.style.display = "flex";
  navRow.style.justifyContent = "space-between";
  navRow.style.width = "100%";
  navRow.style.color = "#fff";
  navRow.style.fontSize = "14px";
  navRow.style.fontWeight = "600";
  navRow.style.marginBottom = "0px";
  navRow.style.paddingLeft = "150px";
  navRow.style.paddingRight = "150px";
  navRow.style.boxSizing = "border-box";

  // Left Text (Back)
  const leftText = document.createElement("span");
  leftText.textContent = backText;
  leftText.style.cursor = "pointer";

  // Right Text (Continue)
  const rightText = document.createElement("span");
  rightText.style.display = "inline-flex";
  rightText.style.alignItems = "center";
  rightText.style.gap = "3px";
  rightText.style.cursor = "pointer";
  appendForwardLabelWithArrow(rightText, forwardText, 12, true);

  navRow.appendChild(leftText);
  navRow.appendChild(rightText);

  // Add nav to container, and container to overlay
  container.appendChild(navRow);
  overlay.appendChild(container);

  // --- Logic & Cleanup ---
  const cleanup = () => {
    try {
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      document.removeEventListener("keydown", handleKeyPress);
    } catch (e) {
      console.warn("Cleanup error:", e);
    }
  };

  const finishAndStart = () => {
    cleanup();
    if (typeof onStart === "function") onStart();
  };

  // if we need back functionality in the future, uncomment this
  // const finishAndBack = () => {
  //   cleanup();
  //   if (typeof onBack === "function") onBack();
  // };

  const handleKeyPress = (event) => {
    // play video: Key "R" - restart and play video
    if (event.code === "KeyR" && videoElement) {
      event.preventDefault();
      videoElement.currentTime = 0;
      videoElement.play();
      return;
    }

    // Start: Key "/" (first press arms + plays, second press advances)
    if (event.code === "Slash" || event.code === "NumpadDivide") {
      event.preventDefault();
      if (videoElement && !slashArmed) {
        slashArmed = true;
        if (videoElement.paused) {
          videoElement.play();
        }
        return;
      }
      finishAndStart();
    }

    // if we need back functionality in the future, uncomment this
    // Back: Key "Z"
    // if (key === "z") {
    //   event.preventDefault();
    //   finishAndBack();
    // }
  };

  document.addEventListener("keydown", handleKeyPress);

  // attach cleanup to element just in case
  overlay.__cleanup = cleanup;

  return overlay;
}

export function createCongratsOverlay({
  imageOrVideoUrl = null,
  onContinue,
  onBack: _onBack,
  forwardText,
  backText,
  playCelebrate = true,
} = {}) {
  // Touch unused optional param to satisfy linter
  void _onBack;
  // Create the main overlay backdrop
  const overlay = document.createElement("div");
  overlay.id = "gear-congrats-overlay";
  overlay.style.position = "fixed";
  overlay.style.top = "0";
  overlay.style.left = "0";
  overlay.style.width = "100%";
  overlay.style.height = "100%";
  overlay.style.display = "flex";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.background = "rgba(0, 0, 0, 0.6)";
  overlay.style.zIndex = "10001";
  overlay.style.fontFamily = "sans-serif";

  let celebrateAudio = null;
  let sadAudio = null;
  if (playCelebrate) {
    celebrateAudio = new Audio("assets/audio/CG01_10_Celebrate_V1.wav");
    celebrateAudio.play().catch(() => {
      // Ignore autoplay-blocked playback in strict browser policies.
    });
  } else {
    sadAudio = new Audio("assets/audio/CG01_03_Sad_V1.wav");
    sadAudio.play().catch(() => { });
  }

  // Create the Card Container
  const container = document.createElement("div");
  container.style.background = "transparent";
  // container.style.borderRadius = "16px";
  container.style.padding = "20px";
  container.style.display = "flex";
  container.style.flexDirection = "column";
  container.style.alignItems = "center";
  container.style.maxWidth = "90%";

  // Image/Video
  let videoElement = null;
  let slashArmed = false;
  if (imageOrVideoUrl) {
    const resolvedUrl = resolveMediaUrl(imageOrVideoUrl);
    // Check if it's a video file based on extension
    const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(resolvedUrl);

    if (isVideo) {
      // Create video wrapper for custom controls
      const videoWrapper = document.createElement("div");
      videoWrapper.style.position = "relative";
      videoWrapper.style.width = "550px";
      videoWrapper.style.height = "330px";
      videoWrapper.style.marginTop = "29px";

      // Create video element without native controls
      const video = document.createElement("video");
      video.src = resolvedUrl;
      video.controls = false;
      video.preload = "metadata";
      video.playsInline = true;
      video.style.width = "100%";
      video.style.height = "100%";
      video.style.display = "block";
      video.style.objectFit = "contain";
      video.style.borderTopLeftRadius = "16px";
      video.style.borderTopRightRadius = "16px";
      video.style.backgroundColor = "transparent";

      // Create custom play button
      const playButton = document.createElement("div");
      playButton.style.position = "absolute";
      playButton.style.top = "50%";
      playButton.style.left = "50%";
      playButton.style.transform = "translate(-50%, -50%)";
      playButton.style.width = "60px";
      playButton.style.height = "60px";
      playButton.style.backgroundColor = "#56FEFF";
      playButton.style.borderRadius = "50%";
      playButton.style.cursor = "pointer";
      playButton.style.display = "flex";
      playButton.style.alignItems = "center";
      playButton.style.justifyContent = "center";
      playButton.style.transition = "all 0.3s ease";
      playButton.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.3)";
      playButton.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style="margin-left: 1px;">
          <path d="M8 5v14l11-7z" fill="#000"/>
        </svg>
      `;

      // Play button hover effect
      playButton.addEventListener("mouseenter", () => {
        playButton.style.backgroundColor = "#6DFFFF";
        playButton.style.transform = "translate(-50%, -50%) scale(1.1)";
      });
      playButton.addEventListener("mouseleave", () => {
        playButton.style.backgroundColor = "#56FEFF";
        playButton.style.transform = "translate(-50%, -50%) scale(1)";
      });

      // Play/pause functionality
      playButton.addEventListener("click", () => {
        if (video.paused) {
          video.play();
          playButton.style.display = "none";
        }
      });

      video.addEventListener("click", () => {
        if (!video.paused) {
          video.pause();
          playButton.style.display = "flex";
        }
      });

      video.addEventListener("play", () => {
        playButton.style.display = "none";
      });

      video.addEventListener("pause", () => {
        playButton.style.display = "flex";
      });

      video.addEventListener("ended", () => {
        playButton.style.display = "flex";
      });

      // Create fullscreen button
      // const fullscreenButton = document.createElement("div");
      // fullscreenButton.style.position = "absolute";
      // fullscreenButton.style.top = "10px";
      // fullscreenButton.style.right = "10px";
      // fullscreenButton.style.width = "36px";
      // fullscreenButton.style.height = "36px";
      // fullscreenButton.style.cursor = "pointer";
      // fullscreenButton.style.display = "flex";
      // fullscreenButton.style.alignItems = "center";
      // fullscreenButton.style.justifyContent = "center";
      // fullscreenButton.style.transition = "all 0.3s ease";
      // fullscreenButton.innerHTML = `
      //   <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      //     <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" fill="#fff" stroke="#000" stroke-width="0.5"/>
      //   </svg>
      // `;

      // // Fullscreen button hover effect
      // fullscreenButton.addEventListener("mouseenter", () => {
      //   fullscreenButton.style.transform = "scale(1.15)";
      // });
      // fullscreenButton.addEventListener("mouseleave", () => {
      //   fullscreenButton.style.transform = "scale(1)";
      // });

      // // Fullscreen functionality
      // fullscreenButton.addEventListener("click", () => {
      //   if (!document.fullscreenElement) {
      //     video.requestFullscreen().catch(err => {
      //       console.log(`Error attempting to enable fullscreen: ${err.message}`);
      //     });
      //   } else {
      //     document.exitFullscreen();
      //   }
      // });

      videoWrapper.appendChild(video);
      videoWrapper.appendChild(playButton);
      // videoWrapper.appendChild(fullscreenButton);
      container.appendChild(videoWrapper);
      videoElement = video;
    } else {
      const img = document.createElement("img");
      img.src = resolvedUrl;
      img.style.maxWidth = "100%";
      img.style.maxHeight = "70vh";
      img.style.display = "block";
      img.style.objectFit = "contain";
      img.style.borderRadius = "8px";
      img.style.boxShadow = "0 10px 30px rgba(0,0,0,0.3)";

      container.appendChild(img);
    }
  }

  // Navigation Text Row
  const navRow = document.createElement("div");
  navRow.style.display = "flex";
  navRow.style.justifyContent = "space-between";
  navRow.style.width = "100%";
  navRow.style.color = "#fff";
  navRow.style.fontSize = "14px";
  navRow.style.fontWeight = "600";
  navRow.style.marginTop = "16px";
  navRow.style.paddingLeft = "20px";
  navRow.style.paddingRight = "20px";
  navRow.style.boxSizing = "border-box";
  navRow.style.position = "relative";

  // Left Text (Back)
  const leftText = document.createElement("span");
  leftText.textContent = backText;
  leftText.style.cursor = "pointer";

  // Right Text (Continue)
  const rightText = document.createElement("span");
  rightText.style.display = "inline-flex";
  rightText.style.alignItems = "center";
  rightText.style.gap = "3px";
  rightText.style.cursor = "pointer";
  appendForwardLabelWithArrow(rightText, forwardText, 12, true);

  // Mirror the continue hint above the existing right-side continue text.
  const elevatedForwardText = document.createElement("span");
  elevatedForwardText.style.display = "inline-flex";
  elevatedForwardText.style.alignItems = "center";
  elevatedForwardText.style.gap = "2px";
  elevatedForwardText.style.position = "absolute";
  elevatedForwardText.style.right = "60px";
  elevatedForwardText.style.bottom = "150px";
  elevatedForwardText.style.color = "#696969";
  elevatedForwardText.style.fontSize = "10px";
  elevatedForwardText.style.fontWeight = "700";
  elevatedForwardText.style.padding = "6px 10px";
  elevatedForwardText.style.borderRadius = "8px";
  elevatedForwardText.style.whiteSpace = "nowrap";
  elevatedForwardText.style.pointerEvents = "none";
  appendForwardLabelWithArrow(elevatedForwardText, forwardText, 10, false);

  navRow.appendChild(leftText);
  navRow.appendChild(rightText);
  navRow.appendChild(elevatedForwardText);

  // Add nav to container, and container to overlay
  container.appendChild(navRow);
  overlay.appendChild(container);

  // --- Logic & Cleanup ---
  const cleanup = () => {
    try {
      if (celebrateAudio) {
        celebrateAudio.pause();
        celebrateAudio.currentTime = 0;
      }
      if (sadAudio) {
        sadAudio.pause();
        sadAudio.currentTime = 0;
      }
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      document.removeEventListener("keydown", handleKeyPress);
    } catch (e) {
      console.warn("Cleanup error:", e);
    }
  };

  const finishAndContinue = () => {
    cleanup();
    if (typeof onContinue === "function") onContinue();
  };

  // if we need back functionality in the future, uncomment this
  // const finishAndBack = () => {
  //   cleanup();
  //   if (typeof onBack === "function") onBack();
  // };

  const handleKeyPress = (event) => {
    // play video: Key "R" - restart and play video
    if (event.code === "KeyR" && videoElement) {
      event.preventDefault();
      videoElement.currentTime = 0;
      videoElement.play();
      return;
    }

    // Continue: Key "/" (first press arms + plays, second press advances)
    if (event.code === "Slash" || event.code === "NumpadDivide") {
      event.preventDefault();
      if (videoElement && !slashArmed) {
        slashArmed = true;
        if (videoElement.paused) {
          videoElement.play();
        }
        return;
      }
      finishAndContinue();
    }

    // if we need back functionality in the future, uncomment this
    // Back: Key "z"
    // if (key === "z") {
    //   event.preventDefault();
    //   finishAndBack();
    // }
  };

  document.addEventListener("keydown", handleKeyPress);

  // attach cleanup to element just in case
  overlay.__cleanup = cleanup;

  return overlay;
}

/**
 * Creates a simple follow-up overlay with just an image and key handler
 */
export function createFollowUpOverlay({
  imageOrVideoUrl = "/assets/images/instructions/video_follow_up.png",
  onContinue,
  forwardText = '[press "/" to continue ->]',
  backText = "",
} = {}) {
  // Simple image-only overlay
  const overlay = document.createElement("div");
  overlay.id = "gear-followup-overlay";
  overlay.style.position = "fixed";
  overlay.style.top = "0";
  overlay.style.left = "0";
  overlay.style.width = "100%";
  overlay.style.height = "100%";
  overlay.style.display = "flex";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.background = "rgba(0, 0, 0, 0.6)";
  overlay.style.zIndex = "10001";
  overlay.style.fontFamily = "sans-serif";

  // Container for image and nav
  const container = document.createElement("div");
  container.style.background = "transparent";
  // container.style.borderRadius = "16px";
  container.style.padding = "20px";
  container.style.display = "flex";
  container.style.flexDirection = "column";
  container.style.alignItems = "center";
  container.style.maxWidth = "90%";

  // image
  if (imageOrVideoUrl) {
    const img = document.createElement("img");
    img.src = imageOrVideoUrl;
    img.style.maxWidth = "100%";
    img.style.maxHeight = "70vh";
    img.style.display = "block";
    img.style.objectFit = "contain";
    img.style.borderRadius = "8px";
    img.style.boxShadow = "0 10px 30px rgba(0,0,0,0.3)";

    container.appendChild(img);
  }

  // Navigation Text Row
  const navRow = document.createElement("div");
  navRow.style.display = "flex";
  navRow.style.justifyContent = "space-between";
  navRow.style.width = "100%";
  navRow.style.color = "#fff";
  navRow.style.fontSize = "14px";
  navRow.style.fontWeight = "600";
  navRow.style.marginTop = "16px";
  navRow.style.paddingLeft = "20px";
  navRow.style.paddingRight = "10px";
  navRow.style.boxSizing = "border-box";
  navRow.style.position = "relative";

  // Left Text (Back)
  const leftText = document.createElement("span");
  leftText.textContent = backText;
  leftText.style.cursor = "pointer";

  // Right Text (Continue)
  const rightText = document.createElement("span");
  rightText.style.display = "inline-flex";
  rightText.style.alignItems = "center";
  rightText.style.gap = "3px";
  rightText.style.cursor = "pointer";
  appendForwardLabelWithArrow(rightText, forwardText, 12, true);

  // Mirror the continue hint above the existing right-side continue text.
  const elevatedForwardText = document.createElement("span");
  elevatedForwardText.style.display = "inline-flex";
  elevatedForwardText.style.alignItems = "center";
  elevatedForwardText.style.gap = "2px";
  elevatedForwardText.style.position = "absolute";
  elevatedForwardText.style.right = "60px";
  elevatedForwardText.style.bottom = "150px";
  elevatedForwardText.style.color = "#696969";
  elevatedForwardText.style.fontSize = "10px";
  elevatedForwardText.style.fontWeight = "700";
  elevatedForwardText.style.padding = "6px 10px";
  elevatedForwardText.style.borderRadius = "8px";
  elevatedForwardText.style.whiteSpace = "nowrap";
  elevatedForwardText.style.pointerEvents = "none";
  appendForwardLabelWithArrow(elevatedForwardText, forwardText, 10, false);

  navRow.appendChild(leftText);
  navRow.appendChild(rightText);
  navRow.appendChild(elevatedForwardText);

  container.appendChild(navRow);
  overlay.appendChild(container);

  // --- Logic & Cleanup ---
  const cleanup = () => {
    try {
      if (overlay && overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      document.removeEventListener("keydown", handleKeyPress);
    } catch (e) {
      console.warn("Cleanup error in followUp:", e);
    }
  };

  const finishAndContinue = () => {
    cleanup();
    if (typeof onContinue === "function") onContinue();
  };

  const handleKeyPress = (event) => {
    // Continue: Key "/"
    if (event.code === "Slash" || event.code === "NumpadDivide") {
      event.preventDefault();
      finishAndContinue();
    }
  };

  document.addEventListener("keydown", handleKeyPress);

  // attach cleanup to element just in case
  overlay.__cleanup = cleanup;

  return overlay;
}
